import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Scissors, Tag, Layers, Eye, Camera, PenTool, Globe, ChevronDown } from 'lucide-react';

const FashionDesign: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Atelier Mode";
    
    return (
        <div className="min-h-screen bg-white font-sans text-black selection:bg-black selection:text-white overflow-x-hidden">
            {/* Minimal Sticky Nav */}
            <nav className="fixed w-full z-50 px-6 py-6 mix-blend-difference text-white top-0 flex justify-between items-baseline">
                <span className="font-bold text-xl tracking-tighter uppercase">{schoolName}</span>
                <div className="hidden md:flex gap-8 text-[10px] font-bold uppercase tracking-[0.2em]">
                     <a href="#" className="hover:underline underline-offset-4 decoration-1">Collections</a>
                     <a href="#" className="hover:underline underline-offset-4 decoration-1">Runway</a>
                     <a href="#" className="hover:underline underline-offset-4 decoration-1">Admissions</a>
                </div>
                <Button variant="outline" className="rounded-none border-white text-white hover:bg-white hover:text-black font-bold uppercase text-[10px] h-8 px-6 tracking-widest bg-transparent">
                    Apply
                </Button>
            </nav>

            {/* Hero Section - Split/Asymmetric */}
            <header className="relative min-h-screen grid grid-cols-1 lg:grid-cols-12 pt-20">
                 {/* Left Text Area */}
                 <div className="lg:col-span-5 p-8 flex flex-col justify-end pb-20 border-r border-black relative">
                     <div className="absolute top-20 left-8">
                         <span className="text-[10px] font-bold uppercase tracking-[0.4em] block mb-2">Est. 1999 • Antwerp</span>
                         <span className="text-[10px] font-bold uppercase tracking-[0.4em] block">Fall / Winter 2026</span>
                     </div>
                     
                     <h1 className="text-7xl md:text-9xl font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
                         De<br/>con<br/>struct.
                     </h1>
                     
                     <p className="text-sm font-medium uppercase tracking-wide max-w-xs mb-12 border-l-2 border-black pl-4">
                         Challenging the silhouette. We teach the rules so you can break them. The definitive school for avant-garde design.
                     </p>
                     
                     <div className="flex flex-col gap-4 items-start">
                         <a href="#" className="group flex items-center gap-4 text-xs font-bold uppercase tracking-widest hover:pl-4 transition-all duration-300">
                             <span className="w-8 h-px bg-black group-hover:w-16 transition-all"></span> Bachelor of Design
                         </a>
                         <a href="#" className="group flex items-center gap-4 text-xs font-bold uppercase tracking-widest hover:pl-4 transition-all duration-300">
                             <span className="w-8 h-px bg-black group-hover:w-16 transition-all"></span> Master of Fine Arts
                         </a>
                     </div>
                 </div>

                 {/* Right Image Area */}
                 <div className="lg:col-span-7 bg-[#f3f3f3] relative overflow-hidden">
                     <img 
                        src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=1976&auto=format&fit=crop" 
                        alt="Fashion Model" 
                        className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 hover:grayscale-0 transition-all duration-700"
                     />
                     <div className="absolute inset-0 bg-white/10 mix-blend-overlay"></div>
                     
                     {/* Floating Badge */}
                     <div className="absolute bottom-8 right-8 bg-black text-white p-6 rotate-[-5deg] hover:rotate-0 transition-transform">
                         <span className="block text-4xl font-bold mb-1">QS #1</span>
                         <span className="block text-[10px] uppercase tracking-widest">Ranked Art School</span>
                     </div>
                 </div>
            </header>

            {/* Marquee */}
            <div className="border-y border-black py-4 overflow-hidden bg-black text-white">
                <div className="animate-marquee whitespace-nowrap opacity-80">
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">Fabric Technology</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">•</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">Sustainable Textiles</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">•</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">Pattern Making</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">•</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">Draping</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">•</span>
                    <span className="text-2xl font-bold uppercase tracking-widest mx-8">Trend Forecasting</span>
                </div>
            </div>

            {/* Departments / Grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 min-h-[50vh]">
                {[
                    { icon: Scissors, label: "Tailoring", desc: "Besokpe construction & fit." },
                    { icon: Layers, label: "Textiles", desc: "Material innovation lab." },
                    { icon: Tag, label: "Marketing", desc: "Luxury brand strategies." },
                    { icon: Camera, label: "Media", desc: "Fashion photography & film." }
                ].map((item, i) => (
                    <div key={i} className="border-r border-b border-black p-10 flex flex-col justify-between hover:bg-black hover:text-white transition-colors group cursor-pointer aspect-square lg:aspect-auto">
                        <item.icon size={32} strokeWidth={1} className="mb-6 group-hover:rotate-12 transition-transform" />
                        <div>
                            <h3 className="text-xl font-bold uppercase tracking-tighter mb-2">{item.label}</h3>
                            <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">{item.desc}</p>
                        </div>
                    </div>
                ))}
            </section>

             {/* Footer */}
            <footer className="bg-white text-black py-20 px-8 border-t border-black">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-end">
                     <div>
                         <h2 className="text-5xl font-black uppercase tracking-tighter mb-8">{schoolName}</h2>
                         <div className="flex gap-8 text-[10px] font-bold uppercase tracking-widest">
                             <a href="#" className="hover:line-through">Instagram</a>
                             <a href="#" className="hover:line-through">LinkedIn</a>
                             <a href="#" className="hover:line-through">Business of Fashion</a>
                         </div>
                     </div>
                     <div className="mt-12 md:mt-0 text-[10px] font-bold uppercase tracking-widest text-right">
                         <p className="mb-2">123 Fashion Ave, Design District</p>
                         <p>© 2026. All rights reserved.</p>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default FashionDesign;
